import { getActiveAccessToken } from "./accessToken";

export class GoogleRequestError extends Error {
  status: number;
  result: unknown;

  constructor(status: number, result: unknown) {
    super(`Google API request failed with status ${status}`);
    this.name = "GoogleRequestError";
    this.status = status;
    this.result = result;
  }
}

export type GoogleResponse<T> = {
  status: number;
  headers: Headers;
  result: T;
};

export async function googleFetch<T>(url: string, init: RequestInit = {}): Promise<GoogleResponse<T>> {
  const token = getActiveAccessToken();
  if (!token) {
    throw new Error("Google access token is missing.");
  }

  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(url, { ...init, headers });
  const text = await response.text();
  let result: T | undefined;
  if (text) {
    try {
      result = JSON.parse(text) as T;
    } catch {
      result = text as T;
    }
  }

  if (!response.ok && response.status !== 308) {
    throw new GoogleRequestError(response.status, result);
  }

  return { status: response.status, headers: response.headers, result: result as T };
}

export async function googleJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await googleFetch<T>(url, init);
  return response.result;
}

export function withQuery(url: string, params: Record<string, string | boolean | undefined>) {
  const parsed = new URL(url);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      parsed.searchParams.set(key, String(value));
    }
  });
  return parsed.toString();
}
